import React from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../api/axios.js';
import { isModuleAccessDeniedError } from '../../../request/moduleAccessDenied.ts';
import { controlsQueryKeys } from '../../../queries/controls/keys.ts';
import { SAMPLE_CHECK_MODES, getSampleCheckMode, type SampleCheckMode } from '../sampleCheckMode.ts';

type Props = {
  control: { sampling_attributes?: unknown } | null | undefined;
  controlUuid?: string;
  onControlUpdated?: () => void;
  readOnly?: boolean;
};

type ApiError = { response?: { data?: { detail?: unknown; message?: unknown; error?: unknown } }; message?: string };

/** The axios interceptor already toasts errors carrying `detail` or `message` (and module-access 403s); toast the
 *  rest here so a failed save is reported exactly once. */
function toastUnreportedError(err: unknown) {
  const data = (err as ApiError)?.response?.data;
  if (typeof data?.detail === 'string' || typeof data?.message === 'string' || isModuleAccessDeniedError(err)) return;
  const text = typeof data?.error === 'string' ? data.error : (err as ApiError)?.message;
  toast.error(text || 'Failed to save');
}

/**
 * "Check each sample" card on the Test Plan tab: whether the test engine checks each sample as one set of documents
 * or line by line (a statement's transactions, each matched to its invoice). Applies to runs executed afterwards; a
 * run already executed keeps its results.
 */
export default function SampleCheckModePanel({ control, controlUuid, onControlUpdated, readOnly = false }: Props) {
  const mode = getSampleCheckMode(control);
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (next: SampleCheckMode) =>
      axiosInstance.put(`/api/sox-controls/${controlUuid}`, { sampling_attributes: { sample_check_mode: next } }),
    onSuccess: () => {
      toast.success('Saved. Runs from now on use this setting.');
      queryClient.invalidateQueries({ queryKey: controlsQueryKeys.all });
      onControlUpdated?.();
    },
    onError: toastUnreportedError,
  });

  const choose = (next: SampleCheckMode) => {
    if (next === mode || save.isPending || !controlUuid) return;
    save.mutate(next);
  };

  const optionCls = (active: boolean) =>
    `flex-1 text-left px-3 py-2 rounded-lg border text-sm transition-colors disabled:opacity-60 ${
      active ? 'border-blue-500 bg-blue-50 text-blue-900' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
    }`;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-6 py-4" data-testid="sample-check-mode-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Check each sample</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Statement controls test every transaction against its support. Most other controls test each sample's
            documents together.
          </p>
        </div>
        {save.isPending && (
          <span className="shrink-0 inline-flex items-center gap-1 text-xs text-gray-500">
            <Loader2 className="h-3 w-3 animate-spin" /> Saving…
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-col sm:flex-row gap-2" role="radiogroup" aria-label="Check each sample">
        {SAMPLE_CHECK_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            role="radio"
            aria-checked={mode === m.value}
            disabled={readOnly || save.isPending}
            onClick={() => choose(m.value)}
            className={optionCls(mode === m.value)}
          >
            <span className="font-medium">{m.label}</span>
            <span className="block text-xs text-gray-500">{m.hint}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
